import type { ComponentType } from 'react';

import type { VisualTemplate } from './system/templateTypes';
import type { CatalogueTemplateDefinition, CatalogueTemplateRegion } from './schema';
import {
  type DeclarativeComponent,
  type DeclarativeRenderConfig,
  type GenericBlockProps,
} from './blocks';

export const wrapComponentWithConfig = <Props extends GenericBlockProps>(
  Component: ComponentType<Props>,
  config: DeclarativeRenderConfig | null,
  displayNameSuffix: string,
): ComponentType<Props> => {
  if (!config) {
    return Component;
  }

  const typedComponent = Component as DeclarativeComponent<Props>;
  const currentConfig = typedComponent.renderBlocks ?? null;
  if (
    currentConfig &&
    currentConfig.slots === config.slots &&
    currentConfig.layout === config.layout &&
    currentConfig.regions === config.regions &&
    currentConfig.layoutByRegion === config.layoutByRegion
  ) {
    return Component;
  }

  const Wrapped = ((props: Props) => <Component {...props} />) as DeclarativeComponent<Props>;
  const componentMeta = Component as { displayName?: string; name?: string };
  Wrapped.displayName = `${componentMeta.displayName ?? componentMeta.name ?? 'Component'}${displayNameSuffix}`;
  Wrapped.renderBlocks = config;
  return Wrapped;
};

export interface CreateDefinitionApplierOptions {
  displayNameSuffix?: string;
}

export const createDefinitionApplier = (
  definition: CatalogueTemplateDefinition | null | undefined,
  { displayNameSuffix = 'Definition' }: CreateDefinitionApplierOptions = {},
): ((template: VisualTemplate) => VisualTemplate) | null => {
  if (!definition) {
    return null;
  }

  const { slots = {}, layout } = definition;
  const regionSet = new Set<CatalogueTemplateRegion>(definition.regions ?? []);

  const hasHeaderRegion = regionSet.has('header');
  const hasPanelRegion = regionSet.has('panel');
  const hasHeroRegion = hasPanelRegion && regionSet.has('hero');

  const headerConfig: DeclarativeRenderConfig | null =
    hasHeaderRegion && slots.header ? { slots: slots.header, layout: layout?.header } : null;

  const heroSlots = hasHeroRegion ? slots.hero : undefined;
  const heroLayout = hasHeroRegion ? layout?.hero : undefined;

  const panelConfig: DeclarativeRenderConfig | null =
    hasPanelRegion || heroSlots
      ? {
          slots: hasPanelRegion ? slots.panel ?? [] : [],
          layout: layout?.panel,
          regions: heroSlots ? { hero: heroSlots } : undefined,
          layoutByRegion: heroLayout ? { hero: heroLayout } : undefined,
        }
      : null;

  const cardRegionSlots: NonNullable<DeclarativeRenderConfig['regions']> = {};
  const cardRegionLayout: NonNullable<DeclarativeRenderConfig['layoutByRegion']> = {};

  (['media', 'content', 'meta', 'footer'] as const).forEach((region) => {
    if (region !== 'meta' && !regionSet.has(region as CatalogueTemplateRegion)) {
      return;
    }
    const regionSlots = slots[region];
    if (regionSlots?.length) {
      cardRegionSlots[region] = regionSlots;
    }

    const regionLayout = layout?.[region];
    if (regionLayout) {
      cardRegionLayout[region] = regionLayout;
    }
  });

  const hasCardRegions = Object.keys(cardRegionSlots).length > 0;
  const hasCardRegionLayout = Object.keys(cardRegionLayout).length > 0;

  const cardConfig: DeclarativeRenderConfig | null =
    slots.card || hasCardRegions || hasCardRegionLayout
      ? {
          slots: slots.card ?? [],
          layout: layout?.card,
          regions: hasCardRegions ? cardRegionSlots : undefined,
          layoutByRegion: hasCardRegionLayout ? cardRegionLayout : undefined,
        }
      : null;

  return (visualTemplate: VisualTemplate): VisualTemplate => {
    const metaOverrides = definition.meta as Partial<VisualTemplate['meta']> | undefined;
    const nextMeta: VisualTemplate['meta'] =
      definition.meta || definition.version
        ? {
            ...(visualTemplate.meta ?? {}),
            ...(metaOverrides ?? {}),
            ...(definition.version ? { version: definition.version } : {}),
          }
        : visualTemplate.meta;

    const WrappedHeader = hasHeaderRegion
      ? (wrapComponentWithConfig(
          visualTemplate.Header,
          headerConfig,
          displayNameSuffix,
        ) as typeof visualTemplate.Header)
      : (() => null) as typeof visualTemplate.Header;

    const WrappedPanel = hasPanelRegion
      ? (wrapComponentWithConfig(visualTemplate.Panel, panelConfig, displayNameSuffix) as typeof visualTemplate.Panel)
      : (() => null) as typeof visualTemplate.Panel;

    return {
      ...visualTemplate,
      Header: WrappedHeader,
      Panel: WrappedPanel,
      Card: wrapComponentWithConfig(visualTemplate.Card, cardConfig, displayNameSuffix) as typeof visualTemplate.Card,
      meta: nextMeta,
    };
  };
};

export const applyDefinitionToVisualTemplate = (
  visualTemplate: VisualTemplate,
  definition: CatalogueTemplateDefinition | null | undefined,
  options?: CreateDefinitionApplierOptions,
): VisualTemplate => {
  const applier = createDefinitionApplier(definition, options);
  return applier ? applier(visualTemplate) : visualTemplate;
};
